import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Button, Box, Text, SimpleGrid } from '@chakra-ui/react';
import { Formik, Form } from 'formik';

import { FormattedMessage, useIntl } from 'react-intl';

import {
    getVariables,
    getVariablesSources,
    createVariableGroup,
    filterVariablesByNameAndSource,
    editVariableGroup,
} from '../../services/studies';

import useCommonToast from '../../hooks/useCommonToast';

import MainModal from '../../components/utils/MainModal';
import Error from '../../components/utils/Error';
import SimpleGridSkeleton from '../../components/utils/SimpleGridSkeleton';
import SearchBar from '../../components/utils/SearchBar';

import InputFormikField from '../forms/InputFormikField';
import CustomCheckbox from '../forms/CustomCheckbox';
import ComboboxField from '../forms/ComboboxField';

const VariableGroupModal = ({
    onCreateVariableGroupSuccess,
    onEditVariableGroupSuccess,
    onClose,
    variableGroup,
    actionType,
}) => {
    const bookId = useSelector((state) => state.connections.book);

    const [variables, setVariables] = useState([]);
    const [selectedVariables, setSelectedVariables] = useState([]);
    const [variableSources, setVariableSources] = useState([]);
    const [selectedVariableSource, setSelectedVariableSource] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredVariables, setFilteredVariables] = useState([]);

    const [isLoading, setIsLoading] = useState(true);
    const [loadingError, setLoadingError] = useState(false);

    const [formFieldsTouched, setFormFieldsTouched] = useState(false);
    const [formValidationError, setFormValidationError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const submitButton = useRef();
    const intl = useIntl();
    const toast = useCommonToast();

    const fetchData = useCallback(async () => {
        const fetchVariables = getVariables();
        const fetchVariablesSources = getVariablesSources();

        return Promise.all([fetchVariables, fetchVariablesSources])
            .then((data) => {
                setVariables(data[0]);
                setFilteredVariables(data[0]);
                setVariableSources(data[1]);
            })
            .catch(() => setLoadingError(true))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        fetchData();

        if (actionType === 'edit') {
            const varIds = variableGroup.variables.map((variable) => variable.variableId);
            setSelectedVariables(varIds);
        }
    }, [fetchData, actionType, variableGroup]);

    useEffect(() => {
        const term = searchTerm.trimStart().toUpperCase();
        let data;

        if (term && term.length >= 2) {
            const vars = selectedVariableSource ? filteredVariables : variables;
            data = filterVariablesByNameAndSource(vars, term, selectedVariableSource);
        } else {
            data = selectedVariableSource
                ? variables.filter((variable) => variable.source === selectedVariableSource)
                : variables;
        }
        setFilteredVariables(data);
    }, [searchTerm]); // eslint-disable-line

    const onSelectVariableSourceChange = (variableSource) => {
        if (selectedVariableSource) {
            setSearchTerm('');
        }
        let data;

        if (variableSource !== '') {
            data = variables.filter((variable) => variable.source === variableSource);
        } else {
            data = variables;
        }

        setSelectedVariableSource(variableSource || null);
        setFilteredVariables(data);
    };

    const onCheckboxStateChange = (id, checked) => {
        if (!formFieldsTouched) {
            setFormFieldsTouched(true);
        }

        if (checked) {
            setSelectedVariables((prev) => [...prev, id]);
        } else {
            setSelectedVariables((prev) => prev.filter((checkedId) => checkedId !== id));
        }
    };

    const onFormSubmit = async ({ groupName: description }) => {
        setIsSubmitting(true);

        const successMessage =
            actionType === 'edit'
                ? intl.formatMessage({ id: 'run_a_study_edit_variable_group_success' })
                : intl.formatMessage({ id: 'run_a_study_create_variable_group_success' });
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        try {
            if (actionType === 'edit') {
                const updatedVariableGroup = await editVariableGroup(variableGroup.variableGroupId, {
                    name: description,
                    selectedVariableIds: selectedVariables,
                });
                setIsSubmitting(false);
                onEditVariableGroupSuccess(updatedVariableGroup);
            } else {
                const newVariableGroup = await createVariableGroup({
                    description,
                    bookId,
                    variableIds: selectedVariables,
                });
                onCreateVariableGroupSuccess(newVariableGroup);
            }
            toast(successMessage);
        } catch (error) {
            if (error.response.data.code === 400) {
                setFormValidationError(true);
                toast({
                    status: 'error',
                    message: error.response.data.error,
                });
            } else {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
            setIsSubmitting(false);
        }
    };

    const validateName = (value) => {
        setFormFieldsTouched(true);

        if (!value) {
            setFormValidationError(true);
            return <FormattedMessage id="common_forms_validation_required" values={{ label: 'Group Name' }} />;
        } else {
            setFormValidationError(false);
        }
    };

    return (
        <MainModal
            size="4xl"
            scrollBehavior="inside"
            isOpen
            onClose={onClose}
            header={
                actionType === 'edit' ? (
                    <FormattedMessage id="run_a_study_edit_variable_group" />
                ) : (
                    <FormattedMessage id="run_a_study_create_a_new_variables_group" />
                )
            }
            content={
                <>
                    <Formik
                        initialValues={{ groupName: actionType === 'edit' ? variableGroup.description : '' }}
                        onSubmit={onFormSubmit}
                    >
                        {() => {
                            return (
                                <Box borderBottom="1px solid" borderColor="gray.100" pb={3}>
                                    <Form>
                                        <InputFormikField
                                            name="groupName"
                                            type="text"
                                            label={<FormattedMessage id="run_a_study_variable_group_name" />}
                                            isRequired
                                            validate={validateName}
                                        />

                                        <Button type="submit" display="none" ref={submitButton} />
                                    </Form>
                                </Box>
                            );
                        }}
                    </Formik>

                    <ComboboxField
                        mt={3}
                        id="variableSource"
                        name="variableSource"
                        label={intl.formatMessage({ id: 'run_a_study_variable_source' })}
                        options={variableSources}
                        showPlaceholder={true}
                        isControlled={false}
                        placeholderLabel="All"
                        placeholderValue=""
                        onChange={onSelectVariableSourceChange}
                    />

                    <Box mt={3}>
                        <SearchBar
                            textHolder={intl.formatMessage({ id: 'run_a_study_search_variables' })}
                            onChange={(value) => setSearchTerm(value)}
                        />
                    </Box>

                    {isLoading ? (
                        <Box mt={3}>
                            <SimpleGridSkeleton rows={12} cols={3} />
                        </Box>
                    ) : loadingError ? (
                        <Box m={6}>
                            <Error
                                primaryId="common_error"
                                secondaryId="common_loading_error"
                                additionalText="variables data"
                            />
                        </Box>
                    ) : (
                        <Box mt={3} pt={3}>
                            <Text size="sm">
                                <FormattedMessage id="run_a_study_select_variables" />
                            </Text>

                            <SimpleGrid mt={3} columns={{ base: 1, md: 3 }} spacing={5}>
                                {filteredVariables.map(({ variableId, varName }) => {
                                    return (
                                        <CustomCheckbox
                                            key={variableId}
                                            isChecked={selectedVariables.includes(variableId)}
                                            onChange={(event) =>
                                                onCheckboxStateChange(variableId, event.target.checked)
                                            }
                                        >
                                            {varName}
                                        </CustomCheckbox>
                                    );
                                })}
                            </SimpleGrid>
                        </Box>
                    )}
                </>
            }
            footerRightSlot={
                <Button
                    isDisabled={
                        !formFieldsTouched || formValidationError || isSubmitting || selectedVariables.length === 0
                    }
                    isLoading={isSubmitting}
                    onClick={() => {
                        submitButton.current.click();
                    }}
                    ml={3}
                >
                    {actionType === 'edit' ? (
                        <FormattedMessage id="common_save_changes_button" />
                    ) : (
                        <FormattedMessage id="run_a_study_create_variable_group" />
                    )}
                </Button>
            }
        />
    );
};

export default VariableGroupModal;
